import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { sleep } from '@ipfabric/ui-components/utils/sleep';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import type {
  DeviceViewElementType,
  DeviceViewEntity,
  DeviceViewEntityId,
} from 'components/DeviceView/deviceViewTypes';
import { useLocale } from 'containers/App/LocaleContext';
import {
  type FC,
  type MouseEvent,
  type ReactElement,
  memo,
  useState,
} from 'react';
import { animations } from 'utils/constants';
import { getTitle } from '../helpers/getTitle';
import { useDeviceViewContextSelectors } from '../model/DeviceViewModel';
import { activateWindow } from '../model/commands/activateWindow';
import { closeDeviceViewEntity } from '../model/commands/closeDeviceViewEntity';
import { maximizeWindow } from '../model/commands/maximizeWindow';
import { minimizeWindow } from '../model/commands/minimizeWindow';

export interface DeviceViewWindowActivateEvent {
  deviceViewElement: DeviceViewElementType;
  deviceViewEntityId: DeviceViewEntityId;
}

export interface DeviceViewWindowMinimizeEvent {
  deviceViewElement: DeviceViewElementType;
  deviceViewEntityId: DeviceViewEntityId;
}

interface Props {
  active: boolean;
  content: ReactElement;
  deviceViewEntity: DeviceViewEntity;
  deviceViewEntityId: DeviceViewEntityId;
  footer?: ReactElement;
  guid: string;
}

const BareDeviceViewWindowContent: FC<Props> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    dispatch: prop('dispatch'),
    handlersContainer: prop('handlersContainer'),
  });
  const [closing, setClosing] = useState<boolean>(false);
  const msg = useLocale();

  const handleWindowClose = useStableCallback(
    async (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setClosing(true);
      await sleep(animations.FASTEST);
      ctxProps.dispatch(closeDeviceViewEntity, {
        deviceViewEntityId: props.deviceViewEntityId,
        deviceViewElement: props.deviceViewEntity.deviceViewElement,
      });
    },
  );

  const handleWindowClick = useStableCallback(() => {
    ctxProps.dispatch(activateWindow, {
      deviceViewEntityId: props.deviceViewEntityId,
    });
    ctxProps.handlersContainer.handleWindowActivate?.({
      deviceViewElement: props.deviceViewEntity.deviceViewElement,
      deviceViewEntityId: props.deviceViewEntityId,
    });
  });

  const handleMaximizeWindow = useStableCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      ctxProps.dispatch(maximizeWindow, {
        deviceViewEntityId: props.deviceViewEntityId,
      });
    },
  );

  const handleMinimizeWindow = useStableCallback(
    async (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setClosing(true);
      await sleep(animations.FASTEST);
      ctxProps.dispatch(minimizeWindow, {
        deviceViewEntityId: props.deviceViewEntityId,
      });
      ctxProps.handlersContainer.handleWindowMinimize?.({
        deviceViewElement: props.deviceViewEntity.deviceViewElement,
        deviceViewEntityId: props.deviceViewEntityId,
      });
    },
  );

  return (
    <div
      className={classNames(
        `window-${props.guid}`,
        'device-view-window box',
        props.active ? 'box-primary' : 'box-default',
        'animated',
        closing ? 'fadeOut' : 'fadeIn',
        'fastest',
      )}
      onClick={handleWindowClick}
    >
      <div className='box-header with-border'>
        <h3 className='box-title'>{getTitle(msg, props.deviceViewEntity)}</h3>
        <div className='pull-right'>
          <i
            className='fa fa-minus btn-box-tool window-no-move cursor-pointer'
            onClick={handleMinimizeWindow}
          />
          <i
            className='fa fa-window-maximize btn-box-tool window-no-move cursor-pointer'
            onClick={handleMaximizeWindow}
          />
          <i
            className='fa fa-xmark btn-box-tool window-no-move cursor-pointer'
            onClick={handleWindowClose}
          />
        </div>
      </div>
      <div className='box-body no-padding window-no-move'>{props.content}</div>
      {props.footer && (
        <div className='box-footer flow-root'>{props.footer}</div>
      )}
    </div>
  );
};

export const DeviceViewWindowContent = memo(BareDeviceViewWindowContent);
